// Debounced slug-availability check.
//
// GET /stations/:slug is already @Public(), so this needs no new endpoint:
// a 200 means the slug is taken, a 404 means it's free. Any other failure
// leaves the status idle rather than blocking — the 409 at save is still the
// real backstop, and a flaky network shouldn't stop someone filling a form.

import { useEffect, useState } from 'react';
import { apiClient, ApiError } from '../../../lib/apiClient';

export type SlugStatus = 'idle' | 'invalid' | 'checking' | 'available' | 'taken';

// Matches the API's INTAKE_SLUG_RE and the DB CHECK.
const SLUG_RE = /^[a-z0-9][a-z0-9-]{1,63}$/;
// Same 400ms as PublisherForm's Publica sub-channel autoload.
const DEBOUNCE_MS = 400;

export function useSlugAvailability(raw: string): {
  status: SlugStatus;
  normalized: string;
} {
  const normalized = raw.trim().toLowerCase();
  const [status, setStatus] = useState<SlugStatus>('idle');

  useEffect(() => {
    if (!normalized) {
      setStatus('idle');
      return;
    }
    if (!SLUG_RE.test(normalized)) {
      setStatus('invalid');
      return;
    }

    let cancelled = false;
    setStatus('checking');
    const timer = setTimeout(async () => {
      try {
        await apiClient.get(`/stations/${encodeURIComponent(normalized)}`, { anonymous: true });
        if (!cancelled) setStatus('taken');
      } catch (e) {
        if (cancelled) return;
        setStatus(e instanceof ApiError && e.status === 404 ? 'available' : 'idle');
      }
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [normalized]);

  return { status, normalized };
}
